import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button } from '@artosera/ui';
import { usePeut } from '../../../../auth/usePeut.js';
import {
  choisirCorrespondance,
  lireProfil,
  lireProfilSources,
  lireSurcharges,
  profilerLot,
  type SuggestionProfil,
} from '../../../../lib/correspondance.js';
import { formaterDate, formaterOctets } from '../../../../lib/format.js';
import { lotEnTravail, type LotReprise } from '../../../../lib/reprise.js';
import pageStyles from '../../../../styles/page.module.css';
import { cleLots } from '../LotsBloc.js';
import { TON_ETAT_LOT, messageReprise } from '../messages.js';
import reprise from '../Reprise.module.css';
import { EditeurChargement, cleProfils } from './EditeurCorrespondance.js';
import { EtapesLot } from './EtapesLot.js';
import { cleProfilSources, ProfilSources } from './ProfilSources.js';
import { TransformationBloc } from './TransformationBloc.js';

/**
 * Le profil que le lot utilise, à SA version : un profil peut avoir changé
 * depuis, le lot garde celle qu'il cite jusqu'à ce qu'on lui demande de
 * passer à la dernière.
 */
function ProfilCite({
  idGalerie,
  lot,
  peutAgir,
  onLot,
}: {
  idGalerie: string;
  lot: LotReprise;
  peutAgir: boolean;
  onLot: (lot: LotReprise) => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const reference = lot.profil;
  const citee = useQuery({
    queryKey: [...cleProfils, reference?.id ?? '', reference?.version ?? 0],
    queryFn: () => lireProfil(reference!.id, reference!.version),
    enabled: reference != null,
  });
  const derniere = useQuery({
    queryKey: [...cleProfils, reference?.id ?? '', 'derniere'],
    queryFn: () => lireProfil(reference!.id),
    enabled: reference != null,
  });
  const [erreur, setErreur] = useState<string | null>(null);
  const passer = useMutation({
    mutationFn: async (version: number) => {
      // Les choix de la galerie suivent le lot d'une version à l'autre.
      const surcharges = lot.surcharges === true ? await lireSurcharges(idGalerie, lot.id) : null;
      return choisirCorrespondance(idGalerie, lot.id, {
        revision: lot.revision,
        profil: { id: reference!.id, version },
        ...(surcharges ? { surcharges } : {}),
      });
    },
    onSuccess: (suite) => {
      setErreur(null);
      onLot(suite);
    },
    onError: (cause) => setErreur(messageReprise(t, cause)),
  });

  if (reference === null || reference === undefined)
    return <p className={reprise.discret}>{t('correspondance.profilCite.aucun')}</p>;
  const plusRecente =
    derniere.data && derniere.data.version > reference.version ? derniere.data : null;

  return (
    <div className={reprise.pile}>
      <p className={reprise.rangee}>
        <span>
          {t('correspondance.profilCite.utilise', {
            nom: citee.data?.nom ?? t('correspondance.profilCite.nomInconnu'),
          })}
        </span>
        <Badge tone="neutral">
          {t('correspondance.profilCite.version', { version: reference.version })}
        </Badge>
      </p>
      {plusRecente ? (
        <div className={reprise.avertissement} role="note">
          <span>
            {t('correspondance.profilCite.plusRecente', {
              version: plusRecente.version,
              actuelle: reference.version,
            })}
          </span>
          {peutAgir ? (
            <div className={reprise.actions}>
              <Button
                variant="secondary"
                size="sm"
                loading={passer.isPending}
                onClick={() => passer.mutate(plusRecente.version)}
              >
                {t('correspondance.profilCite.passer', { version: plusRecente.version })}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
      {erreur ? (
        <p className={reprise.erreur} role="alert">
          {erreur}
        </p>
      ) : null}
    </div>
  );
}

/**
 * La fiche d'un lot tiré d'un dépôt : ses étapes, le profil de ses sources,
 * l'éditeur de correspondance, puis la transformation en morceaux. Le lot
 * parent ne se lance jamais : ses morceaux se lancent un par un, depuis leur
 * propre fiche.
 */
export function LotCorrespondance({
  idGalerie,
  lot,
  onLot,
  onEssaiLance,
}: {
  idGalerie: string;
  lot: LotReprise;
  onLot: (lot: LotReprise) => void;
  onEssaiLance: () => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const client = useQueryClient();
  const [erreur, setErreur] = useState<string | null>(null);
  const peutAgir = peut('console:run-import');
  const peutProfils = peut('console:manage-import-profiles');
  const nature = t(`reprise.natures.${lot.nature}`);
  const profile = lot.profilage?.fait === true;
  const enTravail = lotEnTravail(lot);

  const profilSources = useQuery({
    queryKey: cleProfilSources(idGalerie, lot),
    queryFn: () => lireProfilSources(idGalerie, lot.id),
    enabled: profile,
  });

  const apres = (suite: LotReprise): void => {
    setErreur(null);
    onLot(suite);
    void client.invalidateQueries({ queryKey: cleLots(idGalerie) });
  };
  const profiler = useMutation({
    mutationFn: () => profilerLot(idGalerie, lot.id),
    onSuccess: apres,
    onError: (cause) => setErreur(messageReprise(t, cause)),
  });
  const appliquer = useMutation({
    mutationFn: (suggestion: SuggestionProfil) =>
      choisirCorrespondance(idGalerie, lot.id, {
        revision: lot.revision,
        profil: { id: suggestion.profilId, version: suggestion.version },
      }),
    onSuccess: apres,
    onError: (cause) => setErreur(messageReprise(t, cause)),
  });

  const lancesMorceaux = (lot.morceaux?.liste ?? []).some((morceau) =>
    ['enfile', 'en_cours', 'termine', 'echoue', 'scelle'].includes(morceau.etat),
  );

  return (
    <>
      <div className={pageStyles.panneau}>
        <div className={pageStyles.sectionTitre}>
          <h2>{t('correspondance.fiche.titre', { nature })}</h2>
          <Badge tone={TON_ETAT_LOT[lot.etat]}>{t(`reprise.etats.${lot.etat}`)}</Badge>
        </div>
        <div className={reprise.pile}>
          <p className={reprise.discret}>
            {t('reprise.fiche.meta', {
              date: formaterDate(lot.creeLe, '—'),
              operateur: lot.operateur,
            })}
          </p>
          <EtapesLot lot={lot} />

          <h3 className={reprise.sousTitre}>{t('reprise.fiche.sources')}</h3>
          <ul className={reprise.sources}>
            {lot.sources.map((source) => (
              <li key={source.fichierId ?? source.nom}>
                <span className={reprise.chemin}>{source.nom}</span>
                <span>{formaterOctets(source.octets)}</span>
              </li>
            ))}
          </ul>

          <ProfilCite idGalerie={idGalerie} lot={lot} peutAgir={peutAgir} onLot={apres} />

          {enTravail ? (
            <p className={reprise.discret} aria-live="polite">
              {lot.travail?.type === 'reprise:transformer'
                ? t('correspondance.transformation.enCours')
                : t('correspondance.fiche.profilageEnCours')}
            </p>
          ) : null}
          {!enTravail && lot.travail?.erreur ? (
            <p className={reprise.erreur} role="alert">
              {t('correspondance.fiche.renonce', { message: lot.travail.erreur })}
            </p>
          ) : null}
          {erreur ? (
            <p className={reprise.erreur} role="alert">
              {erreur}
            </p>
          ) : null}

          {peutAgir ? (
            <div className={reprise.actions}>
              <Button
                variant={profile ? 'secondary' : 'primary'}
                loading={profiler.isPending}
                disabled={enTravail || lancesMorceaux}
                onClick={() => profiler.mutate()}
              >
                {profile
                  ? t('correspondance.fiche.reprofiler')
                  : t('correspondance.fiche.profiler')}
              </Button>
            </div>
          ) : null}
          {profile ? (
            <p className={reprise.discret}>{t('correspondance.fiche.reprofilerAide')}</p>
          ) : (
            <p className={reprise.discret}>{t('correspondance.fiche.profilerAide')}</p>
          )}
        </div>
      </div>

      <ProfilSources
        idGalerie={idGalerie}
        lot={lot}
        peutAgir={peutAgir && peutProfils}
        appliquant={appliquer.isPending}
        onAppliquer={(suggestion) => appliquer.mutate(suggestion)}
      />

      {profile ? (
        <section className={pageStyles.panneau} aria-labelledby="corr-editeur">
          <div className={pageStyles.sectionTitre}>
            <h2 id="corr-editeur">{t('correspondance.editeur.titre')}</h2>
          </div>
          <div className={reprise.pile}>
            <p className={reprise.texte}>{t('correspondance.editeur.texte')}</p>
            {!peutProfils ? (
              <p className={reprise.avertissement} role="note">
                {t('correspondance.editeur.droitProfils')}
              </p>
            ) : profilSources.data ? (
              <EditeurChargement
                idGalerie={idGalerie}
                lot={lot}
                profilSources={profilSources.data}
                peutAgir={peutAgir}
                peutProfils={peutProfils}
                onLot={apres}
              />
            ) : profilSources.isError ? (
              <p className={reprise.erreur} role="alert">
                {messageReprise(t, profilSources.error) ?? t('correspondance.profil.erreur')}
              </p>
            ) : (
              <p className={reprise.discret}>{t('reprise.chargement')}</p>
            )}
          </div>
        </section>
      ) : null}

      {profile ? (
        <TransformationBloc
          idGalerie={idGalerie}
          lot={lot}
          peutAgir={peutAgir}
          onLot={apres}
          onEssaiLance={onEssaiLance}
        />
      ) : null}
    </>
  );
}
